import React, { FunctionComponent } from 'react';

import { Translate } from 'react-localize-redux';

import styled from 'styled-components';

import Swiper from 'react-id-swiper';
import 'swiper/css/swiper.css';

import Flex from '@components/layout/flex';
import Typography from '@components/basics/typography';
import CircleRightArrowIcon from '@components/basics/icons/circle-right-arrow';

export interface DashboardCarouselItem {
  img: string;
  title: string;
  text: string;
  link: string;
}

interface DashboardCarouselProps {
  className?: string;
  items: DashboardCarouselItem[];
}

const DashboardCarousel: FunctionComponent<DashboardCarouselProps> = ({
  className,
  items,
}) => {
  const [swiper, setSwiper] = React.useState<any>(null);
  // const [activeIndex, setActiveIndex] = React.useState<number>(0);

  const params = {
    spaceBetween: 24,
    slidePerView: 1,
    centeredSlides: false,
    slidesOffsetBefore: 0,
    slidesOffsetAfter: 0,
    grabCursor: true,
    allowTouchMove: true,
    pagination: {
      el: '.swiper-pagination',
      clickable: true,
    },
  };

  const initSwiper = (instance: any) => {
    if (instance) {
      slideTo(0);
    }
    setSwiper(instance);
  };

  const slideTo = (index: number) => {
    if (swiper) {
      swiper.slideTo(index);
    }
  };

  return (
    <Translate>
      {({ translate }) => (
        <div className={className || ''}>
          <Swiper {...params} getSwiper={initSwiper}>
            {items.map((item, i) => (
              <a
                key={i}
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
              >
                <CarouselItem
                  justifyContent="space-between"
                  alignItems="center"
                  gap={8}
                >
                  <Flex
                    className="carousel-item-wrapper"
                    flexDirection="column"
                    justifyContent="space-between"
                    gap={10}
                  >
                    <div>
                      <Typography className="carousel-item-title" weight="700">
                        {translate(item.title)}
                      </Typography>
                      <Typography
                        className="carousel-item-text"
                        type="small"
                        weight="400"
                      >
                        {translate(item.text)}
                      </Typography>
                    </div>
                    <CircleRightArrowIcon isRed size={24} />
                  </Flex>
                  <img
                    className="carousel-item-img"
                    src={item.img}
                    alt={translate(item.title) as string}
                  />
                </CarouselItem>
              </a>
            ))}
          </Swiper>
        </div>
      )}
    </Translate>
  );
};

export default styled(DashboardCarousel)`
  width: 100%;
  max-width: calc(100vw - 48px);
  position: relative;
  display: block;

  @media ${({ theme }) => theme.device.tablet} {
    max-width: 420px;
  }

  @media ${({ theme }) => theme.device.desktopL} {
    max-width: none;
  }

  .swiper-container {
    --circle-size: 8px;

    .swiper-pagination {
      width: auto;
      text-align: left;
      bottom: 28px;
      left: 64px;
      line-height: var(--circle-size);
      @media ${({ theme }) => theme.device.tablet} {
        bottom: 32px;
      }

      .swiper-pagination-bullet {
        width: var(--circle-size);
        height: var(--circle-size);
        background-color: transparent;
        border: 1px solid ${({ theme }) => theme.colors.tertiary};
        opacity: 1;
        transition: background-color
            ${({ theme }) =>
              `${theme.transitions.duration} ${theme.transitions.type}`},
          border
            ${({ theme }) =>
              `${theme.transitions.duration} ${theme.transitions.type}`},
          width
            ${({ theme }) =>
              `${theme.transitions.duration} ${theme.transitions.type}`},
          border-radius
            ${({ theme }) =>
              `${theme.transitions.duration} ${theme.transitions.type}`};

        &.swiper-pagination-bullet-active {
          width: 16px;
          border-radius: 90px;
          border-color: transparent;
          background-color: ${({ theme }) => theme.colors.primary};
        }
      }
    }
  }
`;

const CarouselItem = styled(Flex)`
  width: 100%;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
  padding: 20px;
  border-radius: 10px;
  height: 142px;
  background-color: ${({ theme }) => theme.colors.box};

  @media ${({ theme }) => theme.device.tablet} {
    padding: 24px;
    height: 165px;
  }

  .carousel-item-wrapper {
    height: 100%;

    .carousel-item-title {
      margin-bottom: 5px;
      @media ${({ theme }) => theme.device.tablet} {
        max-width: 208px;
      }
    }

    .carousel-item-text {
      color: ${({ theme }) => theme.colors.tertiary};
      @media ${({ theme }) => theme.device.tablet} {
        max-width: 154px;
      }
    }
  }

  .carousel-item-img {
    margin-right: 0;
    margin-left: auto;
    width: 96px;
  }
`;
